'use client';

import * as React from 'react';
import { DayPicker } from 'react-day-picker';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { ChevronIcon } from '../../react/components/icons/ChevronIcon';

function Calendar({
  className,
  classNames,
  showOutsideDays = true,
  ...props
}: React.ComponentProps<typeof DayPicker>) {
  return (
    <DayPicker
      showOutsideDays={showOutsideDays}
      className={cn('p-3', className)}
      classNames={{
        months: 'relative flex flex-col sm:flex-row gap-4',
        month: 'w-full',
        month_caption: 'relative mx-10 mb-1 flex h-8 items-center justify-center z-20',
        caption_label: 'text-sm font-medium font-funkis',
        nav: 'absolute top-0 flex w-full justify-between z-10',
        button_previous: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 text-grey hover:text-off-black p-0',
        ),
        button_next: cn(
          buttonVariants({ variant: 'ghost' }),
          'size-8 text-grey hover:text-off-black p-0',
        ),
        weekday: 'size-8 p-0 text-xs font-medium text-grey font-funkis',
        day_button: cn(
          'cursor-pointer relative flex size-8 items-center justify-center whitespace-nowrap rounded-md p-0 text-off-black transition-200',
          'group-[[data-selected]:not(.range-middle)]:[transition-property:color,background-color,border-radius,box-shadow]',
          'group-[[data-selected]:not(.range-middle)]:duration-150',
          'group-data-disabled:pointer-events-none',
          'focus-visible:z-10',
          'hover:not-in-data-selected:bg-dinnerplate-white',
          'group-data-selected:bg-brand-yellow',
          'hover:not-in-data-selected:text-off-black',
          'group-data-selected:text-off-black',
          'group-data-disabled:text-grey-light',
          'group-data-disabled:line-through',
          'group-data-outside:text-grey-light',
          'group-data-selected:group-data-outside:text-off-black',
          'outline-none focus-visible:ring-brand-yellow/50 focus-visible:ring-[3px]',
        ),
        day: 'group size-8 px-0 py-px text-sm font-funkis',
        range_start: 'range-start',
        range_end: 'range-end',
        range_middle: 'range-middle',
        today: cn(
          '*:after:pointer-events-none *:after:absolute *:after:bottom-1 *:after:start-1/2 *:after:z-10',
          '*:after:size-[3px] *:after:-translate-x-1/2 rtl:*:after:translate-x-1/2',
          '*:after:rounded-full *:after:bg-brand-yellow',
          '[&[data-selected]:not(.range-middle)>*]:after:bg-off-black',
          '[&[data-disabled]>*]:after:bg-grey-light',
          '*:after:transition-colors',
        ),
        outside: 'text-grey-light data-selected:bg-butter-yellow/50 data-selected:text-grey',
        hidden: 'invisible',
        week_number: 'size-8 p-0 text-xs font-medium text-grey font-funkis',
        ...classNames,
      }}
      components={{
        Chevron: (props) => {
          if (props.orientation === 'left') {
            return <ChevronIcon direction="left" className="h-4 w-4" />;
          }
          return <ChevronIcon direction="right" className="h-4 w-4" />;
        },
      }}
      {...props}
    />
  );
}

export { Calendar };
